import { useSuspenseQuery } from "@apollo/client";
import {
  Button,
  cn,
  GridTable,
  GridTableBody,
  GridTableCell,
  GridTableContainer,
  GridTableDecorativeRow,
  GridTableHeaderRow,
  GridTableRow,
  GridTableRowGroup,
  LabeledIcon,
  Number,
  PrimaryFullScreenSpinner,
  StatusUI,
  StripLayout,
  TDProgressBar,
  Txt,
} from "@repo/ui";
import { now } from "@repo/utils";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { NewTermDepositDialogTrigger } from "~/components/TermDepositsQueue/new-term-deposit-dialog";
import { graphql } from "~/gql/client";
import type { Currency } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const termDepositsQueueDocument = graphql(`
  query AdminTermDepositsQueuePage {
    organisations {
      id
      name
      businessEntities {
        id
        name
      }
      assets(includeInactive: true, filter: {classes: [TERM_DEPOSIT]}) {
        id
        __typename
        ... on TermDepositAsset {
          id
          name
          currency
          status
          apy
          feeRate
          start
          end
          providerName
          logoUrl
          principleAmount {
            amount
          }
          estimatedValue {
            amount
            currency
          }
          inflightAmount {
            amount
            currency
          }
          businessEntity {
            id
            name
            organisation {
              id
              name
            }
          }
          maturityInstructions {
            __typename
            ... on TermDepositMaturityInstructionsTransfer {
              toAsset {
                id
              }
            }
            ... on TermDepositMaturityInstructionsRolloverPrinciple {
              toAsset {
                id
              }
            }
          }
        }
      }
    }
  }
`);

type Row = {
  id: string;
  name: string;
  currency: Currency;
  principalAmount?: Decimal;
  status:
    | "MATURING"
    | "SCHEDULED"
    | "ACTIVE"
    | "PENDING"
    | "MATURED"
    | "CLOSED"
    | "HOLDING"
    | "DISABLED"
    | "DELETED";
  provider: string;
  providerLogoUrl?: string | null;
  organisationName: string;
  organisationId: string;
  orgHasOneBusinessEntity: boolean;
  businessEntityName: string;
  businessEntityId: string;
  rate: Decimal;
  feeRate?: Decimal | null;
  startUTC: Date;
  endUTC: Date;
  balance: {
    amount: Decimal;
    currency: Currency;
  };
  inFlightNet: {
    amount: Decimal;
    currency: Currency;
  };
  maturityInstructions?:
    | { kind: "rollover" }
    | { kind: "rollover_principal"; assetId: string }
    | { kind: "transfer"; assetId: string };
  amounts?: {
    principal: Decimal;
    yield: Decimal;
    totalValueAtMaturity: Decimal;
  };
};

const statusOrder = [
  "UPCOMING",
  "MATURING",
  "SCHEDULED",
  "ACTIVE",
  "PENDING",
  "MATURED",
  "CLOSED",
  "HOLDING",
  "DISABLED",
  "DELETED",
];

function View() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const selectedTermDepositId = params.termDepositId;
  const { data } = useSuspenseQuery(termDepositsQueueDocument);

  const rows: Row[] = (data.organisations ?? []).flatMap((organisation) =>
    (organisation.assets ?? [])
      .filter((asset) => asset?.__typename === "TermDepositAsset")
      .flatMap((asset) => {
        if (!asset || !asset.businessEntity?.organisation) {
          return [];
        }

        const principal = asset.principleAmount?.amount;
        const totalValueAtMaturity = asset.estimatedValue?.amount;
        const estimatedYield =
          principal && totalValueAtMaturity
            ? totalValueAtMaturity.minus(principal)
            : undefined;

        const syntheticStatus =
          now().plus({ days: 14 }).toJSDate() >= asset.end &&
          asset.status === "ACTIVE"
            ? "MATURING"
            : asset.status;

        return {
          id: asset.id,
          name: asset.name,
          currency: asset.currency,
          principalAmount: principal,
          status: syntheticStatus,
          provider: asset.providerName ?? "",
          providerLogoUrl: asset.logoUrl,
          organisationName: asset.businessEntity.organisation.name,
          organisationId: asset.businessEntity.organisation.id,
          orgHasOneBusinessEntity: organisation.businessEntities?.length === 1,
          businessEntityName: asset.businessEntity.name,
          businessEntityId: asset.businessEntity.id,
          rate: asset.apy ?? new Decimal(0),
          feeRate: asset.feeRate,
          startUTC: asset.start,
          endUTC: asset.end,
          balance: {
            amount: asset.estimatedValue?.amount ?? new Decimal(0),
            currency: asset.estimatedValue?.currency ?? asset.currency,
          },
          inFlightNet: {
            amount: asset.inflightAmount?.amount ?? new Decimal(0),
            currency: asset.inflightAmount?.currency ?? asset.currency,
          },
          maturityInstructions: (() => {
            if (
              asset.maturityInstructions?.__typename ===
              "TermDepositMaturityInstructionsRollover"
            ) {
              return { kind: "rollover" as const };
            }
            if (
              asset.maturityInstructions?.__typename ===
                "TermDepositMaturityInstructionsRolloverPrinciple" &&
              asset.maturityInstructions.toAsset?.id
            ) {
              return {
                kind: "rollover_principal" as const,
                assetId: asset.maturityInstructions.toAsset.id,
              };
            }
            if (
              asset.maturityInstructions?.__typename ===
                "TermDepositMaturityInstructionsTransfer" &&
              asset.maturityInstructions.toAsset?.id
            ) {
              return {
                kind: "transfer" as const,
                assetId: asset.maturityInstructions.toAsset.id,
              };
            }
            return undefined;
          })(),
          amounts:
            principal && estimatedYield && totalValueAtMaturity
              ? {
                  principal,
                  yield: estimatedYield,
                  totalValueAtMaturity,
                }
              : undefined,
        };
      }),
  );
  const organisationOptions = (data.organisations ?? []).map(
    (organisation) => ({
      id: organisation.id,
      name: organisation.name,
      businessEntities: (organisation.businessEntities ?? []).map(
        (businessEntity) => ({
          id: businessEntity.id,
          name: businessEntity.name,
        }),
      ),
    }),
  );

  const groupedTermDeposits = rows
    .sort((a, z) =>
      a.status === "ACTIVE"
        ? a.endUTC.getTime() - z.endUTC.getTime()
        : z.startUTC.getTime() - a.startUTC.getTime(),
    )
    .reduce<Record<string, Row[]>>((acc, termDeposit) => {
      if (
        termDeposit.status === "MATURED" &&
        termDeposit.endUTC < now().minus({ days: 14 }).toJSDate() &&
        termDeposit.balance.amount.equals(0)
      ) {
        return acc;
      }

      const groupKey =
        (termDeposit.status === "SCHEDULED" ||
          termDeposit.status === "PENDING") &&
        DateTime.fromJSDate(termDeposit.startUTC).diffNow().as("days") < 5
          ? "UPCOMING"
          : termDeposit.status;
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(termDeposit);
      return acc;
    }, {});

  const groups = Object.entries(groupedTermDeposits).sort(
    ([a], [z]) => statusOrder.indexOf(a) - statusOrder.indexOf(z),
  );

  return (
    <AdminPageContainer>
      <AdminPageHeader title="Term Deposits Queue" />
      <div className="grid gap-4">
        <div className="flex justify-start">
          <NewTermDepositDialogTrigger
            asChild
            currencyOptions={["AUD"]}
            organisationOptions={organisationOptions}
            onInitiateCreation={({ value: { businessEntityId, currency } }) => {
              void navigate({
                to: "/term-deposits-queue/new",
                search: {
                  businessEntityId,
                  currency,
                } as Record<string, string>,
              });
            }}
          >
            <Button>New term deposit</Button>
          </NewTermDepositDialogTrigger>
        </div>
        <GridTable
          className="grid grid-flow-col gap-4"
          selectedRowId={selectedTermDepositId}
          onSelectedRowIdChange={(rowId) => {
            if (rowId) {
              void navigate({
                to: `/term-deposits-queue/${rowId}`,
              });
            }
          }}
        >
          <GridTableContainer
            className="flex-1"
            columnsTemplate="minmax(max-content,max-content) minmax(min-content,max-content) minmax(5ch,1fr) minmax(min-content,1fr) minmax(min-content,max-content) minmax(min-content,1fr) minmax(min-content,1fr)"
          >
            <GridTableHeaderRow className="mb-4 rounded-lg border">
              <GridTableCell padding="medium">Status</GridTableCell>
              <GridTableCell padding="medium">Client</GridTableCell>
              <GridTableCell padding="medium">Provider</GridTableCell>
              <GridTableCell padding="medium">Term</GridTableCell>
              <GridTableCell padding="medium" align="end">
                Rate
              </GridTableCell>
              <GridTableCell padding="medium" align="end">
                Principal
              </GridTableCell>
              <GridTableCell padding="medium" align="end">
                Balance
              </GridTableCell>
            </GridTableHeaderRow>

            <GridTableBody className="gap-y-6">
              {groups.map(([group, termDeposits]) => (
                <GridTableRowGroup key={group}>
                  <GridTableDecorativeRow className="border-none">
                    <div className="col-span-full pb-1">
                      <Txt variant="column-label">
                        {(() => {
                          if (group === "UPCOMING") {
                            return "STARTING SOON";
                          }
                          if (group === "MATURED") {
                            return "RECENTLY MATURED";
                          }
                          if (group === "MATURING") {
                            return "MATURING SOON";
                          }
                          return group;
                        })()}
                      </Txt>
                    </div>
                  </GridTableDecorativeRow>
                  {termDeposits.map((asset, rowIdx) => (
                    <GridTableRow
                      className={cn(
                        "h-[4.5rem] cursor-pointer border-x border-t",
                        rowIdx === 0 && "rounded-t-lg",
                        rowIdx === termDeposits.length - 1
                          ? "rounded-b-lg border-b"
                          : "",
                      )}
                      rowId={asset.id}
                      key={asset.id}
                    >
                      <GridTableCell padding="medium">
                        <StatusUI status={asset.status} />
                      </GridTableCell>
                      <GridTableCell padding="medium">
                        {asset.organisationName ? (
                          <LabeledIcon name={asset.organisationName} />
                        ) : null}
                      </GridTableCell>
                      <GridTableCell padding="medium">
                        <StripLayout
                          leftContentClassName="self-center"
                          leftContent={
                            <div className="grid items-center">
                              <span className="truncate">{asset.name}</span>
                            </div>
                          }
                          icon={
                            <img
                              className="size-8 rounded"
                              src={asset.providerLogoUrl ?? undefined}
                              alt=""
                            />
                          }
                        />
                      </GridTableCell>
                      <GridTableCell padding="medium" className="">
                        <div className="flex items-center gap-2">
                          <TDProgressBar
                            startDate={asset.startUTC}
                            endDate={asset.endUTC}
                          />

                          <span>
                            (
                            {Math.abs(
                              Math.round(
                                DateTime.fromJSDate(asset.endUTC).diff(
                                  DateTime.fromJSDate(asset.startUTC),
                                  "days",
                                ).days,
                              ),
                            )}
                            ) days
                          </span>
                        </div>
                      </GridTableCell>
                      <GridTableCell padding="medium" align="end">
                        <Number
                          value={asset.rate.div(100)}
                          format={Number.format.percentage({
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                          suffix=" p.a."
                        />
                      </GridTableCell>
                      <GridTableCell padding="medium" align="end">
                        {asset.amounts?.principal ? (
                          <Number
                            value={asset.amounts.principal}
                            format={Number.format.currency(
                              asset.balance.currency,
                            )}
                          />
                        ) : null}
                      </GridTableCell>
                      <GridTableCell padding="medium" align="end">
                        <Number
                          value={asset.balance.amount}
                          format={Number.format.currency(
                            asset.balance.currency,
                          )}
                        />
                      </GridTableCell>
                    </GridTableRow>
                  ))}
                </GridTableRowGroup>
              ))}
            </GridTableBody>
            <GridTableDecorativeRow className="w-full pt-4 text-center">
              <span className="col-span-full text-secondary text-sm">
                Showing active, pending, scheduled, and matured (up to 14 days
                post) term deposits.
              </span>
            </GridTableDecorativeRow>
          </GridTableContainer>
        </GridTable>
      </div>
    </AdminPageContainer>
  );
}

export const Route = createFileRoute("/__auth/term-deposits-queue")({
  head: () => opsHead("Term Deposits Queue"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});
